import { Overlay } from 'app/components/common/overlay'
import PropTypes from 'prop-types'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import * as TelInput from 'react-phone-number-input'
import { getCountryCallingCode } from 'react-phone-number-input'
import Flags from 'react-phone-number-input/flags'
import 'react-phone-number-input/style.css'
import { Input } from '../input'
import styles from './phoneInput.module.scss'

const CountrySelect = ({ value, onChange, options }) => {
  const [showList, setShowList] = useState(false)

  // ensure value changes only from select componenet
  // bug with changing country in middle of phone editing
  const prevValue = useRef()
  useEffect(() => {
    if (!prevValue.current && value) {
      prevValue.current = value
    } else if (prevValue.current !== value) {
      value = prevValue.current
      onChange(prevValue.current)
    }
  }, [value])

  const selectCountry = (country) => {
    setShowList(false)
    // ensure this is the only place where country code changes
    prevValue.current = country
    onChange(country)
  }

  const toggleCountryList = () => {
    setShowList(!showList)
  }

  const countries = useMemo(() => {
    return options.reduce((acc, c) => {
      if (c.value) {
        acc[c.value] = {
          ...c,
          code: getCountryCallingCode(c.value),
        }
      }
      return acc
    }, {})
  }, [''])

  const currentCountry = countries[value]
  const CurrentFlag = Flags[value]

  const countryList = (
    <Overlay isOpen={showList} openChange={setShowList} className={styles.countrySelect__overlay}>
      <ul>
        {Object.keys(countries).map((c) => {
          const co = countries[c]

          const FlagName = Flags[c]

          return (
            <li key={c} onClick={() => selectCountry(c)}>
              <FlagName title={c} />
              <div>
                <span>{co.label}</span>
                <span>+{co.code}</span>
              </div>
            </li>
          )
        })}
      </ul>
    </Overlay>
  )

  const selectedCountryCode = (
    <div className={styles.flagWithCode}>
      {currentCountry && (
        <>
          <CurrentFlag title={currentCountry?.label} />
          <span className={styles.countryCode}>+{currentCountry?.code}</span>
        </>
      )}
    </div>
  )

  return (
    <div className={styles.countrySelect}>
      <button type="button" onClick={toggleCountryList}>
        {selectedCountryCode}
        <svg
          className={`${styles.selectArrow} ${showList ? styles.open : ''}`}
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 8 7"
        >
          <path d="M4,0,8,7H0Z" transform="translate(8 7) rotate(180)" fill="#22233b" />
        </svg>
        <div className={styles.countrySelect__separator} />
      </button>
      {countryList}
    </div>
  )
}

CountrySelect.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  options: PropTypes.array,
}

const PhoneInput = ({ value, onChange, className = '', ...rest }) => {
  // use two states - otherwise country code is reset on empty value
  const [, setValue] = useState('')
  const [phone, setPhone] = useState('')

  const setNumber = (number) => {
    setPhone(number)
    setValue(number || '')

    if (typeof onChange === 'function') {
      onChange(number || '')
    }
  }

  useEffect(() => {
    if (!value) {
      setNumber('')
    }
  }, [value])

  return (
    <div className={`${styles.phoneInput} ${className}`}>
      <TelInput.default
        defaultCountry="US"
        countryOptionsOrder={['US', 'GB', 'CA']}
        international={false}
        inputComponent={Input}
        countrySelectComponent={CountrySelect}
        value={phone}
        onChange={setNumber}
        type="tel"
        autoComplete="tel"
        inputContainerClass={styles.phoneInput_container}
        inputClass={styles.phoneInput_input}
        {...rest}
      />
    </div>
  )
}

PhoneInput.displayName = 'PhoneInput'

PhoneInput.propTypes = {
  id: PropTypes.string,
  label: PropTypes.string,
  name: PropTypes.string,
  className: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func,
}

export { PhoneInput, CountrySelect }
